import { FormEvent, useId, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Building2, ChevronDown, MapPin, Search, WalletCards } from "lucide-react";
import { formatLakhsLabel, textLang } from "@triple-h/shared";
import {useDiscovery} from "../hooks/useDiscovery";
import { useCategories } from "../hooks/useSite";

const budgetOptions = [500, 1_000, 2_000, 3_000, 5_000, 10_000, 20_000, 50_000, 100_000];

type SearchValues = {
  q: string;
  category: string;
  maxPrice: string;
};

type SearchPanelProps = {
  initial?: Partial<SearchValues>;
  tone?: "paper" | "dark";
  compact?: boolean;
  onSearch?: () => void;
};

export function SearchPanel({ initial, tone = "paper", compact = false, onSearch }: SearchPanelProps) {
  const navigate = useNavigate();
  const discovery=useDiscovery();
  const { data: categories = [] } = useCategories();
  const id = useId();
  const [optionsOpen, setOptionsOpen] = useState(Boolean(initial?.category || initial?.maxPrice));
  const [q, setQ] = useState(initial?.q || "");
  const [category, setCategory] = useState(initial?.category || "");
  const [maxPrice, setMaxPrice] = useState(initial?.maxPrice || "");

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const params = new URLSearchParams({ type: "sale" });
    if (q.trim()) params.set("q", q.trim());
    if (category) params.set("category", category);
    if (maxPrice) params.set("maxPrice", maxPrice);
    navigate(`/properties?${params.toString()}`);
    onSearch?.();
  }

  return (
    <form className={`search-panel search-panel-${tone} search-panel-visible ${compact ? "search-panel-compact" : ""}`} onSubmit={submit} role="search" aria-label="Find a property">
      <div className="search-panel-heading"><span className="search-heading-icon"><Search aria-hidden="true" /></span><div><h2>Search properties</h2><p lang="my">သင့်စိတ်ကြိုက် အိမ်ခြံမြေကို ရှာပါ။</p></div></div>
      <div className={`search-fields ${compact ? "search-fields-compact" : ""}`}>
        <div className="search-field search-field-keyword">
          <label htmlFor={`${id}-q`}>Location or keyword</label>
          <div className="search-control">
            <MapPin aria-hidden="true" />
            <input
              id={`${id}-q`}
              name="property-search-q"
              value={q}
              onChange={(event) => setQ(event.target.value)}
              placeholder="တည်နေရာ သို့မဟုတ် အကြောင်းအရာ"
              list={`${id}-locations`}
              autoComplete="off"
            />
          </div>
          <datalist id={`${id}-locations`}>{discovery.data?.locations.map(l=><option key={l.name} value={l.name}/>)}</datalist>
        </div>

        <div id={`${id}-options`} className="search-option-fields" hidden={compact && !optionsOpen}>
        <div className="search-field">
          <label htmlFor={`${id}-category`}>Property type</label>
          <div className="search-control">
            <Building2 aria-hidden="true" />
            <select id={`${id}-category`} value={category} onChange={(event) => setCategory(event.target.value)}>
              <option value="">All properties</option>
              {categories.filter(c=>c.postCount).map((item) => (
                <option key={item.id} value={item.slug} lang={textLang(item.name)}>{item.name}</option>
              ))}
            </select>
            <ChevronDown className="select-caret" aria-hidden="true" />
          </div>
        </div>

        <div className="search-field">
          <label htmlFor={`${id}-budget`}>Maximum budget</label>
          <div className="search-control">
            <WalletCards aria-hidden="true" />
            <select id={`${id}-budget`} value={maxPrice} onChange={(event) => setMaxPrice(event.target.value)}>
              <option value="">Any budget</option>
              {budgetOptions.map((value) => (
                <option key={value} value={value} lang="my">
                  {formatLakhsLabel(value)} အထိ
                </option>
              ))}
            </select>
            <ChevronDown className="select-caret" aria-hidden="true" />
          </div>
        </div>

        </div>

        <button className="search-submit" type="submit">
          <Search aria-hidden="true" />
          <span>{compact ? "Search" : "Search properties"}</span>
          <ArrowRight className="search-arrow" aria-hidden="true" />
        </button>
      </div>
      {compact && <button className="search-options-toggle" type="button" aria-expanded={optionsOpen} aria-controls={`${id}-options`} onClick={() => setOptionsOpen(v => !v)}>Property type & budget{(category || maxPrice) && <span className="search-options-set">Filters set</span>}<ChevronDown aria-hidden="true" /></button>}
    </form>
  );
}
